import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider } from './components/Toast';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { HoyPage } from './pages/Hoy';
import { BaseYPublicacionesPage } from './pages/BaseYPublicaciones';
import { ComprasPage } from './pages/Compras';
import { RemitosPage } from './pages/Remitos';
import { FacturasPage } from './pages/Facturas';
import { PagosPage } from './pages/Pagos';
import { ProveedoresPage } from './pages/Proveedores';
import { InformesPage } from './pages/Informes';
import { UnidadesPage } from './pages/Unidades';
import { BomPage } from './pages/Bom';
import { CondicionPagoPage } from './pages/CondicionPago';
import { ConfiguracionImpositivaPage } from './pages/ConfiguracionImpositiva';
import { RetencionesPage } from './pages/Retenciones';
import { MarcasPage } from './pages/Marcas';
import { VariantesPage } from './pages/Variantes';
import { ConfiguracionMercadoLibrePage } from './pages/ConfiguracionMercadoLibre';
import { PendientesPage } from './pages/Pendientes';
import { FlujoCajaPage } from './pages/FlujoCaja';
import { PlannerPage } from './pages/Planner';
import { UsuariosPage } from './pages/Usuarios';
import { PermisosPage } from './pages/Permisos';
import { ActualizacionPreciosPage } from './pages/ActualizacionPrecios';
import { InsumosPage } from './pages/Insumos';
import { VentasPage } from './pages/Ventas';
import { ReservasDespachosPage } from './pages/ReservasDespachos';
import { DevolucionesPage } from './pages/Devoluciones';
import { ClientesPage } from './pages/Clientes';
import { FacturasVentaPage } from './pages/FacturasVenta';
import { CobrosPage } from './pages/Cobros';
import { InstructivosPage } from './pages/Instructivos';
import { IvaPage } from './pages/Iva';
import { FormacionPreciosPage } from './pages/FormacionPrecios';
import { PreguntasPage as PreguntasMLPage } from './pages/Preguntas';
import { MovimientosStockPage } from './pages/MovimientosStock';
import { ConteosInventarioPage } from './pages/ConteosInventario';
import { MantenimientoPage } from './pages/Mantenimiento';
import { tieneAlguno } from './lib/filterNavItems';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          <p className="mt-3 text-sm text-slate-500">Restaurando sesión...</p>
        </div>
      </div>
    );
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

/** Con `algunoDe` ("modulo:accion"), alcanza con uno de esos permisos (p. ej. Mantenimiento: remitos:ver o wms:ver). */
function PermissionRoute({ children, modulo, accion, algunoDe }: {
  children: React.ReactNode; modulo?: string; accion?: string; algunoDe?: string[];
}) {
  const { isAuthenticated, loading, can } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          <p className="mt-3 text-sm text-slate-500">Restaurando sesión...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const permitido = algunoDe ? tieneAlguno(algunoDe, can) : can(modulo ?? '', accion ?? '');
  if (!permitido) return <Navigate to="/hoy" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, can } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          <p className="mt-3 text-sm text-slate-500">Restaurando sesión...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!can('permisos', 'ver')) return <Navigate to="/hoy" replace />;
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPageWrapper />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><DashboardPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/hoy" element={<ProtectedRoute><Layout><HoyPage /></Layout></ProtectedRoute>} />
      <Route path="/base-publicaciones" element={<PermissionRoute modulo="catalogo" accion="ver"><Layout><BaseYPublicacionesPage /></Layout></PermissionRoute>} />
      <Route path="/compras" element={<PermissionRoute modulo="compras" accion="ver"><Layout><ComprasPage /></Layout></PermissionRoute>} />
      <Route path="/remitos" element={<PermissionRoute modulo="remitos" accion="ver"><Layout><RemitosPage /></Layout></PermissionRoute>} />
      <Route path="/facturas" element={<PermissionRoute modulo="facturas" accion="ver"><Layout><FacturasPage /></Layout></PermissionRoute>} />
      <Route path="/pagos" element={<PermissionRoute modulo="pagos" accion="ver"><Layout><PagosPage /></Layout></PermissionRoute>} />
      <Route path="/proveedores" element={<PermissionRoute modulo="proveedores" accion="ver"><Layout><ProveedoresPage /></Layout></PermissionRoute>} />
      <Route path="/informes" element={<PermissionRoute modulo="informes" accion="ver"><Layout><InformesPage /></Layout></PermissionRoute>} />
      <Route path="/planner" element={<PermissionRoute modulo="planner" accion="ver"><Layout><PlannerPage /></Layout></PermissionRoute>} />
      <Route path="*" element={<Navigate to="/hoy" replace />} />
      <Route path="/unidades" element={<PermissionRoute modulo="unidades" accion="ver"><Layout><UnidadesPage /></Layout></PermissionRoute>} />
      <Route path="/bom" element={<PermissionRoute modulo="bom" accion="ver"><Layout><BomPage /></Layout></PermissionRoute>} />
      <Route path="/condiciones-pago" element={<PermissionRoute modulo="condiciones-pago" accion="ver"><Layout><CondicionPagoPage /></Layout></PermissionRoute>} />
      <Route path="/configuracion-impositiva" element={<PermissionRoute modulo="impositivo" accion="ver"><Layout><ConfiguracionImpositivaPage /></Layout></PermissionRoute>} />
      <Route path="/retenciones" element={<PermissionRoute modulo="retenciones" accion="ver"><Layout><RetencionesPage /></Layout></PermissionRoute>} />
      <Route path="/marcas" element={<PermissionRoute modulo="catalogo" accion="ver"><Layout><MarcasPage /></Layout></PermissionRoute>} />
      <Route path="/variantes" element={<PermissionRoute modulo="catalogo" accion="ver"><Layout><VariantesPage /></Layout></PermissionRoute>} />
      <Route path="/configuracion-mercado-libre" element={<PermissionRoute algunoDe={['catalogo:crear', 'catalogo:eliminar']}><Layout><ConfiguracionMercadoLibrePage /></Layout></PermissionRoute>} />
      <Route path="/pendientes" element={<PermissionRoute modulo="pendientes" accion="ver"><Layout><PendientesPage /></Layout></PermissionRoute>} />
      <Route path="/flujo-caja" element={<PermissionRoute modulo="pagos" accion="ver"><Layout><FlujoCajaPage /></Layout></PermissionRoute>} />
      <Route path="/usuarios" element={<PermissionRoute modulo="usuarios" accion="ver"><Layout><UsuariosPage /></Layout></PermissionRoute>} />
      <Route path="/permisos" element={<AdminRoute><Layout><PermisosPage /></Layout></AdminRoute>} />
      <Route path="/mantenimiento" element={<PermissionRoute algunoDe={['remitos:ver', 'wms:ver']}><Layout><MantenimientoPage /></Layout></PermissionRoute>} />
      {/* Los motivos se administran en Mantenimiento: la ruta vieja lleva ahí. */}
      <Route path="/motivos-movimientos-stock" element={<Navigate to="/mantenimiento" replace />} />
      <Route path="/actualizacion-precios" element={<PermissionRoute modulo="catalogo" accion="ver"><Layout><ActualizacionPreciosPage /></Layout></PermissionRoute>} />
      <Route path="/insumos" element={<PermissionRoute modulo="catalogo" accion="ver"><Layout><InsumosPage /></Layout></PermissionRoute>} />
      <Route path="/ventas" element={<PermissionRoute modulo="ventas" accion="ver"><Layout><VentasPage /></Layout></PermissionRoute>} />
      <Route path="/reservas-despachos" element={<PermissionRoute modulo="ventas" accion="ver"><Layout><ReservasDespachosPage /></Layout></PermissionRoute>} />
      <Route path="/devoluciones" element={<PermissionRoute modulo="ventas" accion="ver"><Layout><DevolucionesPage /></Layout></PermissionRoute>} />
      <Route path="/clientes" element={<PermissionRoute modulo="clientes" accion="ver"><Layout><ClientesPage /></Layout></PermissionRoute>} />
      <Route path="/facturas-venta" element={<PermissionRoute modulo="facturas-venta" accion="ver"><Layout><FacturasVentaPage /></Layout></PermissionRoute>} />
      <Route path="/cobros" element={<PermissionRoute modulo="cobros" accion="ver"><Layout><CobrosPage /></Layout></PermissionRoute>} />
      <Route path="/instructivos" element={<ProtectedRoute><Layout><InstructivosPage /></Layout></ProtectedRoute>} />
      <Route path="/iva" element={<PermissionRoute modulo="impositivo" accion="ver"><Layout><IvaPage /></Layout></PermissionRoute>} />
      <Route path="/formacion-precios" element={<PermissionRoute modulo="catalogo" accion="ver"><Layout><FormacionPreciosPage /></Layout></PermissionRoute>} />
      <Route path="/preguntas-ml" element={<PermissionRoute modulo="preguntas" accion="ver"><Layout><PreguntasMLPage /></Layout></PermissionRoute>} />
      <Route path="/movimientos-stock" element={<PermissionRoute modulo="wms" accion="ver"><Layout><MovimientosStockPage /></Layout></PermissionRoute>} />
      {/* Se cuenta en un solo lugar (DECISIONES 2026-10-02): la ruta de la pantalla vieja de verificar estantes lleva a Conteos de inventario. */}
      <Route path="/verificar-estantes" element={<Navigate to="/conteos-inventario" replace />} />
      <Route path="/conteos-inventario" element={<PermissionRoute modulo="wms" accion="ver"><Layout><ConteosInventarioPage /></Layout></PermissionRoute>} />
    </Routes>
  );
}

function LoginPageWrapper() {
  const { login } = useAuth();
  return <LoginPage onLogin={login} />;
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </AuthProvider>
  );
}
